import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MessageSquareHeart, Search, ThumbsDown, ThumbsUp, X } from "lucide-react";

import Modal from "react-modal";

import Spinner from "../../components/spinner";
import Pagination from "../../components/pagination";
import { useLanguage } from "../../context/languageContext";
import { commentsApi, formatWhen } from "../comments/commentsApi";
import styles from "./feedback.module.scss";

/**
 * Feedback — what readers said when they finished a summary.
 *
 * A thumbs up or down, the quick tags they picked ("Too short", "Confusing"),
 * and anything they typed. Read-only: this is evidence about the writing, and
 * the answer to it is editing the summary, not editing the feedback.
 *
 * Everything narrowing the list is server-side and composes: rating, book
 * title, tag, "had something typed", and one summary. Of 87 rows only 5 carry
 * a comment, which is exactly why that last one is a control rather than
 * something you find by paging.
 */

const PAGE_SIZE = 25;

const RATINGS = [
  { value: "", label: "All" },
  { value: "negative", label: "Needs work" },
  { value: "positive", label: "Liked" },
];

const Feedback = () => {
  const { adminInfo } = useLanguage();
  const api = useMemo(() => commentsApi(adminInfo?.token), [adminInfo?.token]);

  // `?summaryId=` arrives from the summary editor's tally, so "9 liked" lands
  // on those nine rather than on everything. It lives in the URL rather than
  // in state so the filtered view can be linked to and reloaded.
  const [searchParams, setSearchParams] = useSearchParams();
  const summaryId = searchParams.get("summaryId") || undefined;

  const [rating, setRating] = useState("");
  const [tag, setTag] = useState("");
  const [commentedOnly, setCommentedOnly] = useState(false);
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  // Built from the tags readers have actually chosen, so a new chip in the app
  // appears in this picker without a code change here.
  const [availableTags, setAvailableTags] = useState([]);
  // The row opened for reading — the free text is where the useful half of
  // feedback lives, and a table cell is not where you read a paragraph.
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(id);
  }, [search]);

  // Any narrowing invalidates the page number: page 3 of an unfiltered list is
  // usually past the end of a filtered one, which reads as "no results".
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setPage(1);
  }, [rating, tag, commentedOnly, sort, debouncedSearch, summaryId]);

  const load = useCallback(async () => {
    if (!adminInfo?.token) return;
    setLoading(true);
    try {
      const data = await api.feedback({
        page,
        limit: PAGE_SIZE,
        rating: rating || undefined,
        tag: tag || undefined,
        commented: commentedOnly ? "true" : undefined,
        sort,
        q: debouncedSearch || undefined,
        summaryId,
      });
      setRows(data?.feedback || []);
      setTotal(data?.total || 0);
      setPages(data?.totalPages || 1);
      if (data?.availableTags?.length) setAvailableTags(data.availableTags);
    } catch (error) {
      toast.error(error.message);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    adminInfo?.token,
    api,
    page,
    rating,
    tag,
    commentedOnly,
    sort,
    debouncedSearch,
    summaryId,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const narrowed = Boolean(rating || tag || commentedOnly || debouncedSearch || summaryId);

  const clearAll = () => {
    setRating("");
    setTag("");
    setCommentedOnly(false);
    setSearch("");
    setDebouncedSearch("");
    if (summaryId) setSearchParams({});
    setPage(1);
  };

  /** A tag only ever appears on feedback of its own polarity, so the row's
   *  rating is what colours it — no list of words to keep in step. */
  const chipClass = (rowRating) =>
    `${styles.chip} ${rowRating === "positive" ? styles.chipPositive : styles.chipNegative}`;

  const renderTags = (row) =>
    (row.tags || []).map((t) => (
      <span key={t} className={chipClass(row.rating)}>
        {t}
      </span>
    ));

  return (
    <div className="p-3">
      <h4 className="mb-1">Feedback {total > 0 ? `(${total})` : ""}</h4>
      <p className="text-muted small mb-3">
        What readers said on finishing a summary. Read-only — the answer to it
        is editing the summary.
      </p>

      <div className={styles.filters}>
        <div className={`input-group ${styles.search}`}>
          <span className="input-group-text">
            <Search size={14} />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search by book title"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search ? (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>

        <div className="btn-group" role="group" aria-label="Rating">
          {RATINGS.map((option) => (
            <button
              key={option.value || "all"}
              type="button"
              className={`btn ${
                rating === option.value ? "btn-dark" : "btn-outline-dark"
              }`}
              onClick={() => setRating(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <select
          className={`form-select ${styles.selectControl}`}
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          aria-label="Tag"
        >
          <option value="">Any tag</option>
          {availableTags.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <select
          className={`form-select ${styles.selectControl}`}
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          aria-label="Sort"
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>

        <div className={styles.commentCheck}>
          <input
            className="form-check-input mt-0"
            type="checkbox"
            id="commented-only"
            checked={commentedOnly}
            onChange={(e) => setCommentedOnly(e.target.checked)}
          />
          <label htmlFor="commented-only">With a comment</label>
        </div>
      </div>

      {narrowed && (
        <div className={styles.activeFilters}>
          <span>
            {summaryId ? "Showing one summary only." : "Filtered."}{" "}
            {total} {total === 1 ? "result" : "results"}.
          </span>
          <button type="button" className="btn btn-sm btn-outline-dark" onClick={clearAll}>
            {summaryId ? "Show all feedback" : "Clear filters"}
          </button>
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="text-center text-muted py-5">
          <MessageSquareHeart size={28} className="mb-2" />
          <p className="mb-0">
            {narrowed ? "No feedback matches these filters." : "No feedback yet."}
          </p>
        </div>
      ) : (
        <>
          <div className="table_scrollbar table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th className="index">S.no</th>
                  <th>Rating</th>
                  <th>Book summary</th>
                  <th className="max_width">What they said</th>
                  <th>By</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={row.feedbackId}
                    onClick={() => setViewing(row)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td>
                      {row.rating === "positive" ? (
                        <span className="text-success d-inline-flex align-items-center gap-1">
                          <ThumbsUp size={16} /> Liked
                        </span>
                      ) : (
                        <span className="text-danger d-inline-flex align-items-center gap-1">
                          <ThumbsDown size={16} /> Needs work
                        </span>
                      )}
                    </td>
                    <td>
                      {/* The summary being judged, reachable from the
                          judgement. The answer to feedback is editing the
                          summary, and finding it by title in the list was
                          the only route there. */}
                      {row.summaryId ? (
                        <Link
                          to={`/summary/edit/${row.summaryId}`}
                          title="Open this summary"
                          className={styles.bookLink}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.bookTitle || "Untitled summary"}
                        </Link>
                      ) : (
                        row.bookTitle || <span className="text-muted">Unknown</span>
                      )}
                    </td>
                    <td className="max_width">
                      {/* The tags are the feedback when nothing was typed —
                          most readers pick a chip and move on. */}
                      {(row.tags || []).length > 0 ? (
                        <div className="d-flex flex-wrap gap-1 mb-1">{renderTags(row)}</div>
                      ) : null}
                      {row.comment ? (
                        <div className={styles.comment}>{row.comment}</div>
                      ) : null}
                      {!row.comment && (row.tags || []).length === 0 ? (
                        <span className={styles.ratingOnly}>Rating only</span>
                      ) : null}
                    </td>
                    <td>
                      {row.user?.userName || (
                        <span className="text-muted">Deleted account</span>
                      )}
                      {row.user?.email ? (
                        <div className="text-muted small">{row.user.email}</div>
                      ) : null}
                    </td>
                    <td>{formatWhen(row.updatedAt || row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={pages}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={setPage}
              showItemsPerPage={false}
              showTotalItems
            />
          )}
        </>
      )}
      {viewing && (
        <Modal
          isOpen={!!viewing}
          onRequestClose={() => setViewing(null)}
          className="modal_body_md"
          overlayClassName="modal_overlay"
        >
          <div className="box_wrapper">
            <h5 className="mb-1">Feedback</h5>
            <p className="text-muted small mb-3">
              {formatWhen(viewing.updatedAt || viewing.createdAt)}
            </p>

            <div className="mb-3">
              <div className="text-muted small mb-1">On</div>
              <div>
                {viewing.summaryId ? (
                  <Link to={`/summary/edit/${viewing.summaryId}`} className={styles.bookLink}>
                    {viewing.bookTitle || "Untitled summary"}
                  </Link>
                ) : (
                  viewing.bookTitle || "Unknown book"
                )}
              </div>
            </div>

            <div className="mb-3">
              <div className="text-muted small mb-1">Rating</div>
              <div>
                {viewing.rating === "positive" ? (
                  <span className="text-success d-inline-flex align-items-center gap-1">
                    <ThumbsUp size={16} /> Liked
                  </span>
                ) : (
                  <span className="text-danger d-inline-flex align-items-center gap-1">
                    <ThumbsDown size={16} /> Needs work
                  </span>
                )}
              </div>
            </div>

            {(viewing.tags || []).length > 0 ? (
              <div className="mb-3">
                <div className="text-muted small mb-1">They picked</div>
                <div className="d-flex flex-wrap gap-1">{renderTags(viewing)}</div>
              </div>
            ) : null}

            <div className="mb-3">
              <div className="text-muted small mb-1">What they wrote</div>
              {viewing.comment ? (
                <p className={`mb-0 ${styles.comment}`}>{viewing.comment}</p>
              ) : (
                <span className="text-muted">Nothing — a rating only.</span>
              )}
            </div>

            <div className="mb-3">
              <div className="text-muted small mb-1">By</div>
              <div>
                {viewing.user?.userName || (
                  <span className="text-muted">Deleted account</span>
                )}
                {viewing.user?.email ? (
                  <div className="text-muted small">{viewing.user.email}</div>
                ) : null}
              </div>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setViewing(null)}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Feedback;
